import { createStore, type StoreApi } from 'zustand';
import { devtools, type NamedSet as WithDevtools } from 'zustand/middleware';

import { REPUBS_WITH_BOOKIE_LOGOS, REPUBS_WITH_TRANSLATED_BET_NAMES } from '@cx-modules/constants';
import { type Country, type Repub, type StateCode } from '@cx-modules/types';

import {
  type DefaultProps,
  type InitialState,
  type MatchesCouponState,
  type SelectedBet,
  type SyncBet,
} from './interfaces/matches-coupon.interface';

export type MatchesCouponStore = WithDevtools<StoreApi<MatchesCouponState>>;

export const createMatchesCouponStore = ({ oddsFormat = 'american', ...initialProps }: InitialState) => {
  const DEFAULT_PROPS: DefaultProps = {
    user: { oddsFormat },
  };
  const normalisedProps = {
    ...initialProps,
    countryCode: initialProps.countryCode.toUpperCase() as Country,
    repub: initialProps.repub.toUpperCase() as Repub,
    subdivision: initialProps.subdivision.toUpperCase() as StateCode | '',
  };
  const renderOddsButtonBadge =
    normalisedProps.showBookieLogos ?? REPUBS_WITH_BOOKIE_LOGOS.includes(normalisedProps.repub);
  const translateBetNames = REPUBS_WITH_TRANSLATED_BET_NAMES.includes(normalisedProps.repub);

  return createStore<MatchesCouponState>()(
    devtools(set => ({
      ...DEFAULT_PROPS,
      ...normalisedProps,
      addSelectedBet: (bet: SelectedBet) => {
        set((state: { selectedBets?: SelectedBet[] }) => ({
          selectedBets: [...(state.selectedBets || []).filter(b => b.betId !== bet.betId), bet],
        }));
      },
      disableMatchLinks: initialProps.disableMatchLinks ?? false,
      removeAllSelectedBets: () => {
        set({ selectedBets: [] });
      },
      removeSelectedBet: (id: number) => {
        set((state: { selectedBets?: SelectedBet[] }) => ({
          selectedBets: (state.selectedBets || []).filter(b => b.betId !== id),
        }));
      },
      renderOddsButtonBadge,
      selectedBets: [],
      setSeeMoreOddsTooltip: (bool: boolean) => set({ seeMoreOddsTooltip: bool }),
      showEventHeader: initialProps.showEventHeader ?? true,
      showMoreOddsButton: initialProps.showMoreOddsButton ?? true,
      showTeamLogos: initialProps.showTeamLogos,
      syncBetslip: (bets: SyncBet[], overwriteSelections?: boolean) => {
        set((state: { selectedBets?: SelectedBet[] }) => {
          // overwrite existing selections
          if (overwriteSelections) {
            return {
              selectedBets: bets.map(b => ({
                betId: b.betId,
                bookieCode: b.bestOddsBookmakers.split(',')[0],
              })),
            };
          }

          // retain existing selections
          const selectedBets = state.selectedBets || [];
          return {
            selectedBets: [
              ...selectedBets,
              ...bets.reduce((a: SelectedBet[], b: SyncBet) => {
                if (!selectedBets.find(({ betId }) => b.betId === betId)) {
                  a.push({
                    betId: b.betId,
                    bookieCode: b.bestOddsBookmakers.split(',')[0],
                  });
                }
                return a;
              }, []),
            ],
          };
        });
      },
      translateBetNames,
    })),
  );
};
